import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faEnvelope, 
  faIdCard, 
  faInfoCircle,
  faUserMd,
  faHistory,
  faCalendarAlt,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { chatAPI } from '../../../services/api';
import { Skeleton } from '../../Skeleton';
import { ClinicalHistoryEntry, CurrentClinicalInfo } from '../../../types/clinical';
import './UserProfile.css';

interface IUserProfile {
  id: number;
  email: string;
  full_name: string;
  clinical_info?: string;
}

export const UserProfile: React.FC = () => {
  const [userProfile, setUserProfile] = useState<IUserProfile | null>(null);
  const [clinicalHistory, setClinicalHistory] = useState<ClinicalHistoryEntry[]>([]);
  const [currentClinicalInfo, setCurrentClinicalInfo] = useState<CurrentClinicalInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [fontScale, setFontScale] = useState<number>(() => {
    const stored = localStorage.getItem('chat-font-scale');
    return stored ? parseFloat(stored) : 1;
  });

  useEffect(() => {
    loadUserData();
  }, []);

  const increaseFont = () => {
    const newScale = Math.min(fontScale + 0.1, 1.5);
    setFontScale(newScale);
    localStorage.setItem('chat-font-scale', newScale.toString());
  };

  const decreaseFont = () => {
    const newScale = Math.max(fontScale - 0.1, 0.8);
    setFontScale(newScale);
    localStorage.setItem('chat-font-scale', newScale.toString());
  };

  const loadUserData = async () => {
    try {
      setLoading(true);
      const [profile, history, current] = await Promise.all([
        chatAPI.getUserProfile(),
        chatAPI.getClinicalHistory(),
        chatAPI.getCurrentClinicalInfo()
      ]);
      
      setUserProfile(profile);
      setClinicalHistory(history);
      setCurrentClinicalInfo(current);
    } catch (err) {
      setError('Błąd podczas ładowania danych profilu');
      console.error('Error loading user data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="profile-container" style={{ fontSize: `${fontScale}rem` }}>
        <div className="profile-section card">
          <div className="profile-header">
            <div className="profile-header-text">
              <h3><FontAwesomeIcon icon={faUser} /> Twój profil pacjenta</h3>
              <p className="profile-subtitle">Ładowanie informacji o profilu...</p>
            </div>
            <div className="profile-controls">
              <button type="button" className="text-size-button" onClick={increaseFont} aria-label="Zwiększ rozmiar tekstu">A+</button>
              <button type="button" className="text-size-button" onClick={decreaseFont} aria-label="Zmniejsz rozmiar tekstu">A-</button>
            </div>
          </div>
          <div className="profile-content">
            <Skeleton height="3rem" />
            <Skeleton height="2.5rem" />
            <Skeleton height="5rem" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-container" style={{ fontSize: `${fontScale}rem` }}>
        <div className="error-message" role="alert">
          <FontAwesomeIcon icon={faInfoCircle} className="error-icon" />
          {error}
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="profile-container" style={{ fontSize: `${fontScale}rem` }}>
        <div className="profile-section card">
          <div className="no-profile">
            <div className="empty-icon"><FontAwesomeIcon icon={faUser} /></div>
            <p>Nie udało się załadować profilu</p>
            <p className="empty-hint">Spróbuj odświeżyć stronę</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container" style={{ fontSize: `${fontScale}rem` }}>
      <div className="profile-section card" tabIndex={0} aria-label="Profil użytkownika">
        <div className="profile-header">
          <div className="profile-header-text">
            <h3><FontAwesomeIcon icon={faUser} /> Twój profil pacjenta</h3>
            <p className="profile-subtitle">Twoje dane osobowe i informacje medyczne</p>
          </div>
          <div className="profile-controls">
            <button type="button" className="text-size-button" onClick={increaseFont} aria-label="Zwiększ rozmiar tekstu">A+</button>
            <button type="button" className="text-size-button" onClick={decreaseFont} aria-label="Zmniejsz rozmiar tekstu">A-</button>
          </div>
        </div>

        <div className="profile-content">
          <div className="profile-item">
            <div className="profile-icon">
              <FontAwesomeIcon icon={faIdCard} />
            </div>
            <div className="profile-details">
              <span className="profile-label">Imię i nazwisko</span>
              <span className="profile-value">{userProfile.full_name}</span>
            </div>
          </div>
          
          <div className="profile-item">
            <div className="profile-icon">
              <FontAwesomeIcon icon={faEnvelope} />
            </div>
            <div className="profile-details">
              <span className="profile-label">Adres email</span>
              <span className="profile-value">{userProfile.email}</span>
            </div>
          </div>
          
          <div className="profile-item clinical-item">
            <div className="profile-icon">
              <FontAwesomeIcon icon={faUserMd} />
            </div>
            <div className="profile-details">
              <span className="profile-label">Informacje medyczne</span>
              <div className="profile-value clinical-info">
                {userProfile.clinical_info ? (
                  <div className="clinical-content">
                    <div className="current-clinical-info">
                      <ReactMarkdown 
                        components={{
                          p: ({children}) => <p className="markdown-paragraph">{children}</p>,
                          strong: ({children}) => <strong className="markdown-strong">{children}</strong>,
                          em: ({children}) => <em className="markdown-em">{children}</em>,
                          ul: ({children}) => <ul className="markdown-list">{children}</ul>,
                          ol: ({children}) => <ol className="markdown-list">{children}</ol>,
                          li: ({children}) => <li className="markdown-list-item">{children}</li>,
                          h1: ({children}) => <h4 className="markdown-heading">{children}</h4>,
                          h2: ({children}) => <h4 className="markdown-heading">{children}</h4>,
                          h3: ({children}) => <h4 className="markdown-heading">{children}</h4>,
                          h4: ({children}) => <h4 className="markdown-heading">{children}</h4>,
                          h5: ({children}) => <h5 className="markdown-subheading">{children}</h5>,
                          h6: ({children}) => <h6 className="markdown-subheading">{children}</h6>,
                        }}
                      >
                        {userProfile.clinical_info}
                      </ReactMarkdown>
                    </div>
                    {currentClinicalInfo?.last_updated && (
                      <div className="clinical-metadata">
                        <FontAwesomeIcon icon={faCalendarAlt} className="metadata-icon" />
                        <span className="last-updated">
                          Ostatnia aktualizacja: {formatDate(currentClinicalInfo.last_updated)}
                          {currentClinicalInfo.updated_by && currentClinicalInfo.updated_by !== 'system_migration' && (
                            <span className="updated-by"> przez {currentClinicalInfo.updated_by}</span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-clinical-info">
                    <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
                    Brak dostępnych informacji medycznych
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Clinical History Section */}
          {clinicalHistory.length > 1 && (
            <div className="profile-item history-item">
              <div className="profile-icon">
                <FontAwesomeIcon icon={faHistory} />
              </div>
              <div className="profile-details">
                <div className="history-header">
                  <span className="profile-label">Historia zmian informacji medycznych</span>
                  <button
                    className="history-toggle"
                    onClick={() => setShowHistory(!showHistory)}
                    aria-expanded={showHistory}
                    aria-controls="clinical-history"
                  >
                    <FontAwesomeIcon icon={showHistory ? faChevronUp : faChevronDown} />
                    {showHistory ? 'Ukryj historię' : 'Pokaż historię'}
                  </button>
                </div>

                {showHistory && (
                  <div id="clinical-history" className="clinical-history">
                    {clinicalHistory.map((entry, index) => (
                      <div key={entry.id} className="history-entry">
                        <div className="history-header-entry">
                          <span className="history-date">
                            <FontAwesomeIcon icon={faCalendarAlt} className="date-icon" />
                            {formatDate(entry.created_at)}
                          </span>
                          <span className="history-author">
                            przez {entry.created_by === 'system_migration' ? 'System' : entry.created_by}
                          </span>
                        </div>
                        <div className="history-content">
                          <ReactMarkdown 
                            components={{
                              p: ({children}) => <p className="markdown-paragraph">{children}</p>,
                              strong: ({children}) => <strong className="markdown-strong">{children}</strong>,
                              em: ({children}) => <em className="markdown-em">{children}</em>,
                              ul: ({children}) => <ul className="markdown-list">{children}</ul>,
                              ol: ({children}) => <ol className="markdown-list">{children}</ol>,
                              li: ({children}) => <li className="markdown-list-item">{children}</li>,
                              h1: ({children}) => <h4 className="markdown-heading">{children}</h4>,
                              h2: ({children}) => <h4 className="markdown-heading">{children}</h4>,
                              h3: ({children}) => <h4 className="markdown-heading">{children}</h4>,
                              h4: ({children}) => <h4 className="markdown-heading">{children}</h4>,
                              h5: ({children}) => <h5 className="markdown-subheading">{children}</h5>,
                              h6: ({children}) => <h6 className="markdown-subheading">{children}</h6>,
                            }}
                          >
                            {entry.clinical_info}
                          </ReactMarkdown>
                        </div>
                        {entry.notes && (
                          <div className="history-notes">
                            <FontAwesomeIcon icon={faInfoCircle} className="notes-icon" />
                            <span className="notes-text">{entry.notes}</span>
                          </div>
                        )}
                        {index === 0 && (
                          <div className="current-indicator">
                            <span>Aktualne</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="profile-footer">
          <div className="profile-note">
            
            <p><FontAwesomeIcon icon={faInfoCircle} className="note-icon" />Jeśli potrzebujesz zaktualizować swoje dane, skontaktuj się z lekarzem lub administratorem systemu.</p>
          </div>
        </div>
      </div>
    </div>
  );
};
